import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";
import { SERVICES, waLink } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-ocean/15 bg-white px-4 py-3 text-base text-ink outline-none transition placeholder:text-mutedink/50 focus:border-turquoise focus:ring-2 focus:ring-turquoise/30";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ocean/70";

const EMPTY = {
  full_name: "", email: "", phone: "", service: "",
  destination: "", travel_dates: "", travellers: "", budget: "", requirements: "",
};

export const EnquiryForm = ({ initialService = "" }) => {
  const [form, setForm] = useState({ ...EMPTY, service: initialService });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong while sending your enquiry. Please try again, or reach us directly on WhatsApp."
      );
      setStatus("error");
    }
  };

  if (status === "success") {
    const waMsg = `Hi ATO, I am ${form.full_name}. I just submitted an enquiry for ${form.service}${form.destination ? ` (${form.destination})` : ""}. Looking forward to hearing from you.`;
    return (
      <div data-testid="enquiry-success-message" className="rounded-3xl bg-white p-8 text-center shadow-xl ring-1 ring-turquoise/20 sm:p-10">
        <CheckCircle2 className="mx-auto h-14 w-14 text-turquoise" />
        <h3 className="mt-4 font-display text-2xl font-bold text-ink">Thank you, {form.full_name.split(" ")[0]}!</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-mutedink">
          Your enquiry has been received. Our team will get in touch with you shortly
          to discuss your travel requirements.
        </p>
        <a
          href={waLink(waMsg)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="enquiry-success-whatsapp-button"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
        >
          <MessageCircle className="h-4 w-4" />
          Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="travel-enquiry-form"
      className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-ocean/5 sm:p-8"
      noValidate={false}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enquiry-name" className={labelCls}>Full Name *</label>
          <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2}
            value={form.full_name} onChange={update("full_name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-email" className={labelCls}>Email Address *</label>
          <input id="enquiry-email" type="email" data-testid="enquiry-email-input" required
            value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-phone" className={labelCls}>Phone / WhatsApp Number *</label>
          <input id="enquiry-phone" type="tel" data-testid="enquiry-phone-input" required minLength={6}
            value={form.phone} onChange={update("phone")} placeholder="+91 ..." className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-service" className={labelCls}>Service Required *</label>
          <select id="enquiry-service" data-testid="enquiry-service-select" required
            value={form.service} onChange={update("service")} className={inputCls}>
            <option value="" disabled>Select a service</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="enquiry-destination" className={labelCls}>Preferred Destination</label>
          <input id="enquiry-destination" data-testid="enquiry-destination-input"
            value={form.destination} onChange={update("destination")} placeholder="e.g. Maldives, Kerala, Europe" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-dates" className={labelCls}>Approximate Travel Dates</label>
          <input id="enquiry-dates" data-testid="enquiry-dates-input"
            value={form.travel_dates} onChange={update("travel_dates")} placeholder="e.g. 15–22 December 2026" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-travellers" className={labelCls}>Number of Travellers</label>
          <input id="enquiry-travellers" data-testid="enquiry-travellers-input"
            value={form.travellers} onChange={update("travellers")} placeholder="e.g. 2 adults, 1 child" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-budget" className={labelCls}>Approximate Budget</label>
          <input id="enquiry-budget" data-testid="enquiry-budget-input"
            value={form.budget} onChange={update("budget")} placeholder="e.g. ₹1.5 lakh total" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="enquiry-requirements" className={labelCls}>Additional Requirements</label>
          <textarea id="enquiry-requirements" data-testid="enquiry-requirements-input" rows={4}
            value={form.requirements} onChange={update("requirements")}
            placeholder="Tell us anything else — hotel preferences, occasions, special needs..."
            className={`${inputCls} resize-none`} />
        </div>
      </div>

      {status === "error" && (
        <p data-testid="enquiry-error-message" className="mt-4 rounded-xl bg-coral/10 px-4 py-3 text-sm font-medium text-coral">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="enquiry-submit-button"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-turquoise px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-turquoise/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-emeraldsea disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
        ) : (
          <><Send className="h-4 w-4" /> Send Enquiry</>
        )}
      </button>
    </form>
  );
};
